import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Play, ArrowRight, Pause, AlertCircle } from 'lucide-react';
import { api, type AccountRow, type AiSummaryConfig } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface StatusResponse {
  enabled: boolean;
  model: string;
  schedule: string;
  ai_marker: string;
}

export function AiSummary(): JSX.Element {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [newConfig, setNewConfig] = useState({
    kommo_account_id: '',
    display_name: 'AI Weekly Summary',
  });

  const { data: status } = useQuery({
    queryKey: ['ai-summary-status'],
    queryFn: () => api.get<StatusResponse>('/admin/ai-summary/status'),
  });

  const { data: configs = [], isLoading } = useQuery({
    queryKey: ['ai-summary-configs'],
    queryFn: () => api.get<AiSummaryConfig[]>('/admin/ai-summary/configs'),
    refetchInterval: 10_000,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<AccountRow[]>('/admin/accounts'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof newConfig) =>
      api.post<{ id: string }>('/admin/ai-summary/configs', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-summary-configs'] });
      setIsOpen(false);
      setNewConfig({ kommo_account_id: '', display_name: 'AI Weekly Summary' });
    },
  });

  const runNowMutation = useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean; job_id: string }>(
      `/admin/ai-summary/configs/${id}/run-now`,
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-summary-configs'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'paused' }) =>
      api.patch<{ ok: boolean }>(`/admin/ai-summary/configs/${id}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-summary-configs'] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI Weekly Summary</h1>
          <p className="text-sm text-muted-foreground">
            Claude пишет в Kommo сводку по сделкам выбранных воронок каждую неделю.
          </p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button disabled={accounts.length === 0 || !status?.enabled}>
              <Plus className="h-4 w-4" />
              Создать конфиг
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate(newConfig);
              }}
              className="space-y-4"
            >
              <DialogHeader>
                <DialogTitle>Новый AI summary</DialogTitle>
                <DialogDescription>
                  После создания вы попадёте на страницу настройки воронок и статусов.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="account">Kommo аккаунт</Label>
                <select
                  id="account"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={newConfig.kommo_account_id}
                  onChange={(e) =>
                    setNewConfig((s) => ({ ...s, kommo_account_id: e.target.value }))
                  }
                  required
                >
                  <option value="">— выбрать —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.display_name} ({a.subdomain})
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Название</Label>
                <Input
                  id="name"
                  value={newConfig.display_name}
                  onChange={(e) =>
                    setNewConfig((s) => ({ ...s, display_name: e.target.value }))
                  }
                  required
                />
              </div>
              {createMutation.error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                  {(createMutation.error as Error).message}
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Создаём…' : 'Создать'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span>Раздел</span>
            <Badge variant={status?.enabled ? 'success' : 'secondary'}>
              {status?.enabled ? 'Активен' : 'Не сконфигурирован'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!status && <div className="text-sm text-muted-foreground">Загрузка…</div>}
          {status && (
            <dl className="grid gap-2 text-sm sm:grid-cols-3">
              <ConfigRow label="Модель Claude" value={status.model} />
              <ConfigRow label="Расписание" value={status.schedule} />
              <ConfigRow label="AI-маркер" value={status.ai_marker} mono />
            </dl>
          )}
          {status && !status.enabled && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div>
                Чтобы включить раздел, добавьте <code className="font-mono">ANTHROPIC_API_KEY</code>{' '}
                в <code className="font-mono">/etc/bridge/api.env</code> и перезапустите{' '}
                <code className="font-mono">bridge bridge-worker</code>.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Конфиги</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <div className="text-sm text-muted-foreground">Загрузка…</div>}
          {!isLoading && configs.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Пока нет конфигов. Нажмите «Создать конфиг» сверху.
            </div>
          )}
          {configs.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Название</TableHead>
                  <TableHead>Аккаунт Kommo</TableHead>
                  <TableHead className="text-right">Воронок</TableHead>
                  <TableHead className="w-24">Статус</TableHead>
                  <TableHead>Последний запуск</TableHead>
                  <TableHead className="w-64 text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.display_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {c.account_display_name} <span className="text-muted-foreground/70">({c.account_subdomain})</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {c.pipeline_ids.length || '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={c.status === 'active' ? 'success' : 'secondary'}>
                        {c.status === 'active' ? 'Active' : 'Paused'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {c.last_run_at ? new Date(c.last_run_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          title={c.status === 'active' ? 'Pause' : 'Resume'}
                          onClick={() =>
                            toggleMutation.mutate({
                              id: c.id,
                              status: c.status === 'active' ? 'paused' : 'active',
                            })
                          }
                        >
                          {c.status === 'active' ? (
                            <>
                              <Pause className="h-3.5 w-3.5" /> Pause
                            </>
                          ) : (
                            <>
                              <Play className="h-3.5 w-3.5" /> Resume
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Run now"
                          disabled={!status?.enabled || runNowMutation.isPending}
                          onClick={() => runNowMutation.mutate(c.id)}
                        >
                          <Play className="h-3.5 w-3.5" /> Run now
                        </Button>
                        <Button asChild size="sm">
                          <Link to={`/ai-summary/${c.id}`}>
                            Открыть
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ConfigRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : 'text-sm'}>{value}</dd>
    </div>
  );
}
